/**
 * Google Calendar event normalization + day grouping. PURE — no React, no
 * fetch, no env. This is the part with the date bugs in it, so it is the part
 * that is testable in isolation.
 *
 * THE TRAP THIS FILE EXISTS TO AVOID: an all-day event arrives as
 * `start.date = '2026-09-23'`. `new Date('2026-09-23')` parses that as UTC
 * midnight, which is Sept 22 at 6pm in Denver — so every all-day event would
 * render a day early across the Americas. Every date-only string here goes
 * through `parseLocalDateKey`.
 */

import { compareDateKeys, localDateKey, parseLocalDateKey, startOfLocalDay } from '../date';

export interface CalendarEvent {
  /** Unique across calendars: Google ids are only unique within one. */
  readonly key: string;
  readonly calendarId: string;
  readonly title: string;
  /** Epoch ms. For an all-day event, local midnight of its first day. */
  readonly start: number;
  readonly end: number;
  readonly allDay: boolean;
  /** Per-calendar colour from calendarList. A runtime hex, never a literal. */
  readonly color: string | null;
}

export interface DayGroup {
  readonly dateKey: string;
  readonly events: readonly CalendarEvent[];
}

/* ----------------------------- wire types ----------------------------- */

export interface GoogleDateTime {
  /** RFC3339 with offset, e.g. '2026-09-23T14:00:00-06:00'. Timed events. */
  readonly dateTime?: string;
  /** 'YYYY-MM-DD'. All-day events. End is EXCLUSIVE. */
  readonly date?: string;
}

export interface GoogleEvent {
  readonly id?: string;
  readonly status?: string;
  readonly summary?: string;
  readonly start?: GoogleDateTime;
  readonly end?: GoogleDateTime;
}

export interface GoogleCalendarListEntry {
  readonly id?: string;
  readonly summary?: string;
  readonly backgroundColor?: string;
  readonly primary?: boolean;
  readonly selected?: boolean;
  readonly deleted?: boolean;
  readonly accessRole?: string;
}

/* ----------------------------- the window ----------------------------- */

/**
 * "Today's events + next 2 days at a glance" — three local calendar days.
 * Returned as date keys so every consumer agrees on the boundaries.
 */
export const WINDOW_DAYS = 3;

export function windowDateKeys(from: Date = new Date()): string[] {
  const base = startOfLocalDay(from);
  const keys: string[] = [];
  for (let i = 0; i < WINDOW_DAYS; i += 1) {
    keys.push(localDateKey(new Date(base.getFullYear(), base.getMonth(), base.getDate() + i)));
  }
  return keys;
}

/** Local midnight today. RFC3339 via toISOString() is a UTC *instant* — correct. */
export function windowTimeMin(from: Date = new Date()): string {
  return startOfLocalDay(from).toISOString();
}

/** Local midnight + 3 days, constructed (not +259_200_000) so DST is exact. */
export function windowTimeMax(from: Date = new Date()): string {
  const base = startOfLocalDay(from);
  return new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate() + WINDOW_DAYS,
    0,
    0,
    0,
    0,
  ).toISOString();
}

/* --------------------------- normalization ---------------------------- */

function instant(v: GoogleDateTime | undefined): { ms: number; allDay: boolean } | null {
  if (v === undefined) return null;
  if (typeof v.dateTime === 'string' && v.dateTime !== '') {
    // RFC3339 carries an explicit offset, so Date() is unambiguous here.
    const ms = new Date(v.dateTime).getTime();
    return Number.isNaN(ms) ? null : { ms, allDay: false };
  }
  if (typeof v.date === 'string' && v.date !== '') {
    const d = parseLocalDateKey(v.date);
    return d === null ? null : { ms: d.getTime(), allDay: true };
  }
  return null;
}

export function normalizeEvent(
  raw: GoogleEvent,
  calendarId: string,
  color: string | null,
): CalendarEvent | null {
  if (raw.status === 'cancelled') return null;

  const start = instant(raw.start);
  const end = instant(raw.end);
  if (start === null) return null;

  const title =
    typeof raw.summary === 'string' && raw.summary.trim() !== ''
      ? raw.summary.trim()
      : '(no title)';

  const id = typeof raw.id === 'string' && raw.id !== '' ? raw.id : `${calendarId}:${start.ms}`;

  return {
    key: `${calendarId}::${id}::${start.ms}`,
    calendarId,
    title,
    start: start.ms,
    end: end?.ms ?? start.ms,
    allDay: start.allDay,
    color,
  };
}

/**
 * Bucket events into the given days.
 *
 * All-day events are EXPANDED across their span, because Google's `end.date`
 * is exclusive and a three-day conference should appear on all three days,
 * not only the first. Timed events are placed on the local day they start —
 * an event running past midnight belongs to the evening it began.
 *
 * Within a day: all-day first, then chronological, then by title so the order
 * is deterministic across refreshes (two 9am meetings must not swap places).
 */
export function groupEventsByDay(
  events: readonly CalendarEvent[],
  dateKeys: readonly string[],
): DayGroup[] {
  const buckets = new Map<string, CalendarEvent[]>();
  for (const key of dateKeys) buckets.set(key, []);

  for (const event of events) {
    if (!event.allDay) {
      buckets.get(localDateKey(new Date(event.start)))?.push(event);
      continue;
    }
    // end.date is exclusive; a single-day event has end === start + 1 day.
    const last = Math.max(event.start, event.end - 1);
    for (const key of dateKeys) {
      const day = parseLocalDateKey(key);
      if (day === null) continue;
      const dayStart = day.getTime();
      const dayEnd = new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate() + 1,
        0,
        0,
        0,
        0,
      ).getTime();
      if (event.start < dayEnd && last >= dayStart) buckets.get(key)?.push(event);
    }
  }

  return dateKeys
    .slice()
    .sort(compareDateKeys)
    .map((dateKey) => ({
      dateKey,
      events: (buckets.get(dateKey) ?? []).sort(sortWithinDay),
    }));
}

function sortWithinDay(a: CalendarEvent, b: CalendarEvent): number {
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
  if (a.start !== b.start) return a.start - b.start;
  return a.title.localeCompare(b.title);
}

/**
 * Which calendars to read. Google omits `selected` when a calendar is hidden
 * in the user's UI, so respecting it keeps the panel showing the same
 * calendars the user already chose to see. `primary` is always included.
 */
export function selectableCalendars(
  entries: readonly GoogleCalendarListEntry[],
  max: number,
): GoogleCalendarListEntry[] {
  return entries
    .filter(
      (c) =>
        typeof c.id === 'string' && c.id !== '' && c.deleted !== true && (c.selected === true || c.primary === true),
    )
    .sort((a, b) => Number(b.primary === true) - Number(a.primary === true))
    .slice(0, max);
}

/** id -> backgroundColor, from ONE calendarList call. Colours are runtime hex. */
export function colorMap(
  entries: readonly GoogleCalendarListEntry[],
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const c of entries) {
    if (typeof c.id === 'string' && typeof c.backgroundColor === 'string' && c.backgroundColor !== '') {
      map.set(c.id, c.backgroundColor);
    }
  }
  return map;
}
